/**
 * rateLenses.js — the six scoring lenses /rate's manual entry offers, and the
 * ONE reference lens every percentile is measured under.
 *
 * Ben's Augie's league is Standard scoring with 6pt passing touchdowns, and the
 * form had no way to say either thing: it offered three platform DEFAULTS, all
 * of them half PPR and 4pt, so a standard-scoring room was graded on points it
 * does not play for. The grid below is the two facts people actually differ on,
 * crossed.
 *
 * 🔴 TWO LENSES DO TWO DIFFERENT JOBS AND MUST NOT BE CONFUSED.
 *   DISPLAY lens  — the user's own scoring. Every number they can see is under
 *                   it: projected points, PPG, position ranks, the season
 *                   column. Their team, their rules.
 *   REFERENCE lens — Half PPR, 4pt pass TD, fixed. Every PERCENTILE is computed
 *                   under it, on both sides. A percentile is a comparison, and
 *                   a comparison where each side is scored by different rules
 *                   is not a comparison at all.
 * See rateTeam.js for the split in code and rateTeamDists.json's meta.lens for
 * the lens the artifact was built with.
 *
 * 🔴 WHY HALF PPR / 4pt IS THE REFERENCE. It is the corpus mode: Sleeper's own
 * default, which most harvested rooms run. Picking the modal lens means the
 * reference re-scoring moves the fewest real teams the least distance from
 * what they actually played, so the arrays stay close to the rooms they came
 * from. It is NOT a claim that this scoring is correct or standard.
 *
 * THE BASE IS SLEEPER'S DEFAULT, not DEFAULT_SCORING. The house default scores
 * passing yards at one point per 20, where every real platform uses 25; a lens
 * grid whose "Standard, 4pt" is nobody's standard would be worse than no grid.
 * Only receptions and passTD vary across the six.
 */

import { DEFAULT_SCORING } from "./scoring.js";

/**
 * What every lens in the grid shares: Sleeper's default for everything that is
 * not one of the two facts the grid varies.
 */
const LENS_BASE = {
  passYdsPer: 25,
  passInt: -2,
  rushTD: 6,
  recTD: 6,
  fumbles: 0,
  fumblesLost: -2,
};

/**
 * Reception values, and how the copy says them out loud. No jargon, no "PPR 0.5".
 * Exported because the settings form offers these as a row of their own.
 */
export const RECEPTION_KINDS = [
  { slug: "std", receptions: 0, word: "Standard" },
  { slug: "half", receptions: 0.5, word: "Half PPR" },
  { slug: "full", receptions: 1, word: "Full PPR" },
];

export const PASS_TD_KINDS = [4, 6];

/**
 * The key format, in ONE place.
 *
 * The settings form picks the two halves separately and recombines them, so if
 * it string-split the key itself there would be two definitions of what a lens
 * key looks like and only one of them would get updated.
 */
export function lensKeyOf(slug, passTD) {
  return `${slug}-${passTD}`;
}

/** A lens key back into its two facts. An unknown key reads as the reference. */
export function splitLensKey(key) {
  const hit = RATE_LENSES.find((l) => l.key === key);
  const use = hit || RATE_LENSES.find((l) => l.key === REFERENCE_LENS_KEY);
  return { slug: use.slug, passTD: use.passTD };
}

/**
 * The six lenses, in grid order: reception kind outer, pass TD inner, so the
 * two settings of one scoring family sit next to each other.
 *
 * `key` is stable and stored in component state; `label` is the two facts and
 * is the only thing a user ever reads.
 */
export const RATE_LENSES = RECEPTION_KINDS.flatMap((rec) =>
  PASS_TD_KINDS.map((passTD) => ({
    key: lensKeyOf(rec.slug, passTD),
    slug: rec.slug,
    passTD,
    receptionWord: rec.word,
    passTdWord: `${passTD}pt pass TD`,
    label: `${rec.word}, ${passTD}pt pass TD`,
    changes: { ...LENS_BASE, receptions: rec.receptions, passTD },
  }))
);

/**
 * The corpus mode, and the default selection on the manual form.
 * 🔴 CHANGING THIS MEANS REGENERATING rateTeamDists.json. The artifact records
 * the lens it was built under in meta.lens, and rateTeam asserts nothing about
 * it, so a silent change here would compare a user's team against arrays
 * scored by different rules. Regenerate and commit the two together.
 */
export const REFERENCE_LENS_KEY = "half-4";

/** A lens key -> a complete house scoring object. Unknown key -> the reference. */
export function lensFor(key) {
  const hit = RATE_LENSES.find((l) => l.key === key) || RATE_LENSES.find((l) => l.key === REFERENCE_LENS_KEY);
  return { ...DEFAULT_SCORING, ...hit.changes };
}

/** How the copy names a lens. Unknown key -> the reference lens's label. */
export function lensLabel(key) {
  const hit = RATE_LENSES.find((l) => l.key === key);
  return hit ? hit.label : lensLabel(REFERENCE_LENS_KEY);
}

/** The one lens every percentile on this page is computed under, both sides. */
export const REFERENCE_LENS = lensFor(REFERENCE_LENS_KEY);

/** The reference lens said out loud, for the artifact header and the face line. */
export const REFERENCE_LENS_LABEL = lensLabel(REFERENCE_LENS_KEY);
